import { useState, useCallback, useRef, useEffect } from "react";
import { cleanTextForSpeech } from "@/utils/textCleaner";

interface UseBrowserTTSOptions {
  language?: string;
  rate?: number;
  pitch?: number;
  voiceUri?: string;
  onError?: (error: Error) => void;
}

interface UseBrowserTTSReturn {
  speak: (text: string) => void;
  stop: () => void;
  isPlaying: boolean;
  isSupported: boolean;
  voices: SpeechSynthesisVoice[];
  error: Error | null;
}

// Split text into chunks for reliable playback (browser TTS has length limits)
const splitTextIntoChunks = (text: string, maxLength: number = 180): string[] => {
  const chunks: string[] = [];
  const sentences = text.split(/(?<=[.!?。！？])\s+/);
  
  for (const sentence of sentences) {
    if (sentence.length <= maxLength) {
      chunks.push(sentence);
    } else {
      const parts = sentence.split(/(?<=[,;:，；：])\s*/);
      let currentChunk = "";
      
      for (const part of parts) {
        if (currentChunk.length + part.length <= maxLength) {
          currentChunk += (currentChunk ? " " : "") + part;
        } else {
          if (currentChunk) chunks.push(currentChunk);
          if (part.length > maxLength) {
            const words = part.split(/\s+/);
            currentChunk = "";
            for (const word of words) {
              if (currentChunk.length + word.length + 1 <= maxLength) {
                currentChunk += (currentChunk ? " " : "") + word;
              } else {
                if (currentChunk) chunks.push(currentChunk);
                currentChunk = word;
              }
            }
          } else {
            currentChunk = part;
          }
        }
      }
      if (currentChunk) chunks.push(currentChunk);
    }
  }
  
  return chunks.filter(c => c.trim().length > 0);
};

export const useBrowserTTS = (options: UseBrowserTTSOptions = {}): UseBrowserTTSReturn => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);
  const chunksQueueRef = useRef<string[]>([]);
  const isStoppedRef = useRef(false);
  const hasUserInteractedRef = useRef(false);

  const isSupported = typeof window !== "undefined" && "speechSynthesis" in window;

  // Mobile wake-up pattern - iOS blocks autoplay
  useEffect(() => {
    if (typeof window === "undefined" || !isSupported) return;
    
    const handleInteraction = () => {
      if (!hasUserInteractedRef.current) {
        hasUserInteractedRef.current = true;
        console.log("[Browser TTS] User interaction detected - TTS unlocked");
        
        // Wake up speechSynthesis with silent utterance
        try {
          const silentUtterance = new SpeechSynthesisUtterance("");
          silentUtterance.volume = 0;
          window.speechSynthesis.speak(silentUtterance);
          window.speechSynthesis.cancel();
        } catch (e) {
          // Ignore errors
        }
      }
    };

    window.addEventListener("click", handleInteraction, { passive: true });
    window.addEventListener("touchstart", handleInteraction, { passive: true });
    window.addEventListener("keydown", handleInteraction, { passive: true });

    return () => {
      window.removeEventListener("click", handleInteraction);
      window.removeEventListener("touchstart", handleInteraction);
      window.removeEventListener("keydown", handleInteraction);
    };
  }, [isSupported]);

  // Load voices
  useEffect(() => {
    if (!isSupported) return;

    const loadVoices = () => {
      const availableVoices = window.speechSynthesis.getVoices();
      setVoices(availableVoices);
      console.log("[Browser TTS] Voices loaded:", availableVoices.length);
    };

    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
  }, [isSupported]);

  const stop = useCallback(() => {
    if (!isSupported) return;
    
    isStoppedRef.current = true;
    chunksQueueRef.current = [];
    window.speechSynthesis.cancel();
    utteranceRef.current = null;
    setIsPlaying(false);
  }, [isSupported]);

  const speakChunk = useCallback((
    text: string, 
    onEnd: () => void,
    onChunkError: (error: string) => void
  ) => {
    const utterance = new SpeechSynthesisUtterance(text);
    utteranceRef.current = utterance;

    utterance.lang = options.language || "ru-RU";
    utterance.rate = options.rate || 1.0;
    utterance.pitch = options.pitch || 1.0;

    const availableVoices = window.speechSynthesis.getVoices();
    
    if (options.voiceUri) {
      const selectedVoice = availableVoices.find(v => v.voiceURI === options.voiceUri);
      if (selectedVoice) utterance.voice = selectedVoice;
    } else {
      const langPrefix = (options.language || "ru").substring(0, 2);
      // Prefer Google voices (higher quality), then any voice for language
      const googleVoice = availableVoices.find(
        (v) => v.lang.startsWith(langPrefix) && v.name.includes("Google")
      );
      const anyVoice = availableVoices.find((v) => v.lang.startsWith(langPrefix));
      const preferredVoice = googleVoice || anyVoice;
      
      if (preferredVoice) {
        utterance.voice = preferredVoice;
        console.log("[Browser TTS] Using voice:", preferredVoice.name);
      }
    }

    utterance.onend = () => onEnd();
    utterance.onerror = (event) => {
      if (event.error !== "interrupted" && event.error !== "canceled") {
        onChunkError(event.error);
      } else {
        onEnd();
      }
    };

    window.speechSynthesis.speak(utterance);
  }, [options.language, options.rate, options.pitch, options.voiceUri]);

  const speak = useCallback((text: string) => {
    if (!isSupported) {
      const err = new Error("Web Speech API не поддерживается");
      setError(err);
      options.onError?.(err);
      return;
    }

    if (!text || text.length < 5) return;

    stop();
    isStoppedRef.current = false;
    setError(null);

    try {
      const cleanedText = cleanTextForSpeech(text);
      
      if (window.speechSynthesis.paused) {
        window.speechSynthesis.resume();
      }
      window.speechSynthesis.cancel();
      
      const chunks = splitTextIntoChunks(cleanedText);
      if (chunks.length === 0) return;

      chunksQueueRef.current = [...chunks];
      let chunkIndex = 0;
      
      setIsPlaying(true);

      const playNextChunk = () => {
        if (isStoppedRef.current || chunkIndex >= chunks.length) {
          setIsPlaying(false);
          utteranceRef.current = null;
          chunksQueueRef.current = [];
          return;
        }

        const chunk = chunks[chunkIndex];
        chunkIndex++;
        
        speakChunk(
          chunk,
          () => playNextChunk(),
          (errorMsg) => {
            setIsPlaying(false);
            const err = new Error(`Ошибка синтеза речи: ${errorMsg}`);
            setError(err);
            options.onError?.(err);
          }
        );
      };

      playNextChunk();
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Неизвестная ошибка TTS");
      setError(error);
      options.onError?.(error);
    }
  }, [isSupported, options, stop, speakChunk]);

  return { speak, stop, isPlaying, isSupported, voices, error };
};
